import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useGetCalculatedMarketShopCart } from '@/api/market-shop/checkout/get-calculated-market-shop-cart'
import { useGetSettingsByToken } from '@/api/market-shop/client/get-settings-by-token'
import { Button } from '@/components/business/market-shop/button'
import { NoteTextarea } from '@/components/business/market-shop/note-textarea'
import { PopupWindow } from '@/components/business/market-shop/popup-window'
import { Icon } from '@/components/ui/icon'
import { PopupRequiredProps, popupFactory } from '@/factories'
import { CheckOut } from '@/pages/market-shop/check-out'
import { MAX_NOTE_LENGTH } from '@/pages/market-shop/market-shop.constants'
import { formatPrice } from '@/utils'

import { EmptyState } from '../empty-state'
import { useMarketShopStore } from '../market-shop.store'
import { CartProductItem } from './cart-product-item'
import * as S from './shopping-cart.styled'

export type ShoppingCartProps = PopupRequiredProps

export const ShoppingCart = ({ onClose }: ShoppingCartProps) => {
  const { t } = useTranslation(['market-shop'])
  const marketShopStore = useMarketShopStore()
  const isShopOpened = marketShopStore.isOpened
  const hasProductsInCart = marketShopStore.hasProductsInCart()
  const cartNote = marketShopStore.note

  const handleChangeNote = (note: string) => {
    marketShopStore.setNote(note)
  }

  const { isResidentFlow } = useGetSettingsByToken()

  const {
    subTotalPrice,
    taxPrice,
    totalPrice,
    calculatedCartMap,
    discount,
    isFetching: isCartFetching,
  } = useGetCalculatedMarketShopCart(marketShopStore.getMappedCartRequest())

  const handleCheckoutClick = () => {
    popupFactory.open(CheckOut, {}, { suspendCurrent: true })
  }

  return (
    <PopupWindow lightBg={true} onClose={onClose}>
      {() => (
        <>
          <S.Content>
            {!hasProductsInCart ? (
              <EmptyState
                title={t('market-shop:shoppingCart.emptyTitle')}
                message={t('market-shop:shoppingCart.emptyDescription')}
              />
            ) : (
              <>
                {Object.values(marketShopStore.cart).map(({ product, quantity, note }) => (
                  <CartProductItem
                    key={product.id}
                    product={product}
                    quantity={quantity}
                    note={note}
                    tax={calculatedCartMap.get(product.id)?.taxAmount ?? 0}
                    disabled={!isShopOpened}
                    onAddToCart={marketShopStore.addProduct}
                    onSubtractFromCart={marketShopStore.subtractProduct}
                    onRemoveFromCart={marketShopStore.removeProduct}
                  />
                ))}
                {isResidentFlow && (
                  <NoteTextarea
                    label={t('market-shop:shoppingCart.noteLabel')}
                    placeholder={t('market-shop:shoppingCart.notePlaceholder')}
                    maxLength={MAX_NOTE_LENGTH}
                    value={cartNote}
                    disabled={!isShopOpened}
                    onChange={handleChangeNote}
                  />
                )}
                <S.PricesContainer>
                  <S.Price>
                    <span>{t('market-shop:shoppingCart.subTotal')}</span>
                    <span>{formatPrice(subTotalPrice, { zeroPrice: true })}</span>
                  </S.Price>
                  {discount > 0 && (
                    <S.Price>
                      <span>{t('market-shop:shoppingCart.discount')}</span>
                      <span>-{formatPrice(discount, { zeroPrice: true })}</span>
                    </S.Price>
                  )}
                  <S.Price>
                    <span>{t('market-shop:shoppingCart.tax')}</span>
                    <span>{formatPrice(taxPrice, { zeroPrice: true })}</span>
                  </S.Price>
                  <S.Price>
                    <span>{t('market-shop:shoppingCart.total')}</span>
                    <span>{formatPrice(totalPrice, { zeroPrice: true })}</span>
                  </S.Price>
                </S.PricesContainer>
              </>
            )}
          </S.Content>
          <S.Controls>
            <Button onlyBox use="secondary-gray" size="large" onClick={onClose}>
              <Icon icon={CaretLeftIcon} />
            </Button>
            <Button
              use="primary"
              size="large"
              disabled={!hasProductsInCart || !isShopOpened || cartNote.length > MAX_NOTE_LENGTH}
              loading={isCartFetching}
              onClick={handleCheckoutClick}
            >
              {t('market-shop:shoppingCart.checkoutButton')}
              <Icon icon={CaretRightIcon} />
            </Button>
          </S.Controls>
        </>
      )}
    </PopupWindow>
  )
}
